import { Toaster, toast } from "react-hot-toast";

import Matter from "matter-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { GameEngine } from "react-game-engine";

import { GameState } from "./common/Enums";
import WorldManager from "./managers/WorldManager";
import PhysicsManager from "./managers/PhysicsManager";
import GameManager from "./managers/GameManager";
import {
  generatePipes,
  generatePlayer,
  generateWorld,
  resetWorld,
} from "./generators/WorldGenerator";
import { Constants } from "./common/Constants";

import "./styles/app.min.css";
import ReactGA from "react-ga4";

// hooks
import { useAuth } from "./hooks/useAuth";

// analytics
export const analytics = require("react-segment");

const coin = require("./assets/coin.png");

const coinsPerScore = 3;

const App = () => {
  analytics.default.load(process.env.REACT_APP_SEGMENT_ID);
  ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS_ID as string);
  ReactGA.event({
    category: "App Actions",
    action: "App Start",
    label: "Start", // optional
    value: 1, // optional, must be a number
    nonInteraction: true, // optional, true/false
  });

  const {
    coinCenter,
    fetchCoinCenter,
    startArcadeGameSession,
    endArcadeGameSession,
    arcadeGameDetail,
  } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const gameRef = useRef<any>();
  const [gameState, setGameState] = useState<GameState>(GameState.MainMenu);
  const [score, setScore] = useState(0);
  const [entities, setEntities] = useState<Entities>();
  const [highScore, setHighScore] = useState(0);
  const costCoins = arcadeGameDetail?.cost_coins;

  const setupWorld = useCallback(() => {
    let matterEngine = Matter.Engine.create();
    Matter.World.clear(matterEngine.world, false);

    let gameEntities: Entities = {
      physics: {
        engine: matterEngine,
        world: matterEngine.world,
      },
    };

    gameEntities["flyer"] = generatePlayer(matterEngine.world);
    gameEntities = generateWorld(matterEngine.world, gameEntities);
    gameEntities = generatePipes(matterEngine.world, gameEntities);

    setEntities(gameEntities);

    Matter.Events.on(matterEngine, "collisionStart", () => {
      gameRef.current.dispatch({ type: "gameOver" });
    });

    return gameEntities;
  }, []);

  useEffect(() => {
    if (gameRef.current === null) return;

    window.onblur = () => {
      if (gameState === GameState.Running)
        gameRef.current.dispatch({ type: "pauseGame" });
    };

    window.onfocus = () => {
      if (gameState === GameState.Paused)
        gameRef.current.dispatch({ type: "resumeGame" });
    };
  }, [gameRef, gameState]);

  const prepareGame = async () => {
    try {
      await startArcadeGameSession(process.env.REACT_APP_FLYING_ELEMENTS_ID!);

      let newWorld = setupWorld();

      if (gameState === GameState.GameOver) {
        gameRef.current.swap(newWorld);
      }

      setGameState(GameState.Standby);
    } catch (error) {
      toast.error(
        (error as any)?.response?.data?.detail ||
          (error as any)?.response?.data?.errors[0]?.msg ||
          "Something went wrong"
      );
    }
  };

  const startGame = () => {
    Matter.Body.setVelocity(entities?.flyer.body, {
      x: entities?.flyer.body.velocity.x,
      y: -Constants.JUMP_HEIGHT,
    });
    setGameState(GameState.Running);
  };

  const onEvent = async (e: Event) => {
    switch (e.type) {
      case "pauseGame":
        setGameState(GameState.Paused);
        break;
      case "resumeGame":
        setGameState(GameState.Running);
        break;
      case "gameOver":
        if (score > highScore) setHighScore(score);
        setGameState(GameState.GameOver);
        setIsLoading(true);
        try {
          await endArcadeGameSession({
            earned_coins: score * coinsPerScore,
            score,
          });
          await fetchCoinCenter();
        } catch (error) {
          toast.error(
            (error as any)?.response?.data?.detail ||
              (error as any)?.response?.data?.errors[0]?.msg ||
              "Something went wrong"
          );
        }
        setIsLoading(false);

        break;
      case "score":
        setScore(score + 1);
        break;
    }
  };

  const resetGame = () => {
    resetWorld();
    prepareGame();
    setScore(0);
  };

  const LiquidCoins = (
    <div
      className="value"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        margin: "20px",
      }}
    >
      <div>Liquid Coins: </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          style={{
            maxWidth: "100%",
            margin: "5px",
          }}
          width="20"
          height="20"
          src={coin}
          alt="AssetDash Coin"
        />
        <div>{isLoading ? "Loading..." : coinCenter?.liquid_coins}</div>
      </div>
    </div>
  );

  if (gameState === GameState.MainMenu) {
    return (
      <div className="container">
        <Toaster position="top-right" reverseOrder={true} />
        <div className="fullscreen main-menu">
          <h2 className="asset-dash">
            Asset<span>Dash</span>
          </h2>
          <h1 className="game-title">Flying Elements</h1>
          <button className="btn start-btn" onClick={prepareGame}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {costCoins !== undefined && costCoins > 0 ? (
                <>
                  <div>Play</div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      style={{
                        maxWidth: "100%",
                        margin: "5px",
                      }}
                      width="20"
                      height="20"
                      src={coin}
                      alt="AssetDash Coin"
                    />
                    {costCoins}
                  </div>
                </>
              ) : (
                <>
                  <div>Free Play</div>
                </>
              )}
            </div>
          </button>
          {LiquidCoins}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <Toaster position="top-right" reverseOrder={true} />
      <div className="background" />
      <GameEngine
        ref={gameRef}
        className="gameContainer"
        systems={[WorldManager, PhysicsManager, GameManager]}
        running={gameState === GameState.Running}
        onEvent={onEvent}
        entities={entities}
      />
      <h1 className={`score ${gameState === GameState.GameOver ? "hide" : ""}`}>
        {score}
      </h1>
      <div
        className={`bottom-barrier ${
          gameState === GameState.Running || gameState === GameState.Standby
            ? "animated"
            : ""
        }`}
      />
      {gameState === GameState.Standby && (
        <div className="fullscreen standby-screen" onClick={startGame}>
          <h2 className="tap-container">Tap To Move</h2>
        </div>
      )}
      {gameState === GameState.Paused && (
        <div className="fullscreen paused">
          <h1>Game Paused</h1>
        </div>
      )}
      {gameState === GameState.GameOver && (
        <div className="fullscreen">
          <div className="game-over">
            <h1>Game Over</h1>
            <div className="stats" style={{ width: "100%" }}>
              <div
                style={{
                  width: "33%",
                  position: "relative",
                  display: "inline-block",
                }}
              >
                <h2 className="stat">Score</h2>
                <h1 className="value">{score}</h1>
              </div>
              <div
                style={{
                  width: "33%",
                  position: "relative",
                  display: "inline-block",
                }}
              >
                <h2 className="stat">Coins</h2>
                <h1 className="value">
                  <img src={coin} alt="AssetDash Coin" height={24} />{" "}
                  {score * coinsPerScore}
                </h1>
              </div>
              <div
                style={{
                  width: "33%",
                  position: "relative",
                  display: "inline-block",
                }}
              >
                <h2 className="stat">Best</h2>
                <h1 className="value">{highScore}</h1>
              </div>
            </div>
            <button className="btn retry-btn" onClick={resetGame}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {costCoins !== undefined && costCoins > 0 ? (
                  <>
                    <div>Start New Game</div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <img
                        style={{
                          maxWidth: "100%",
                          margin: "5px",
                        }}
                        width="20"
                        height="20"
                        src={coin}
                        alt="AssetDash Coin"
                      />
                      {costCoins}
                    </div>
                  </>
                ) : (
                  <div>Start New Game</div>
                )}
              </div>
            </button>
            {LiquidCoins}
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
